import React, { FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UiNode } from '@ory/kratos-client';
import Stack from '@mui/material/Stack';


import {
  SubmitResponse,
  BodyValues,
  sessionQueryKey
} from './common';

import FormNode, { getNodeKey, getNodeInitialValue } from './FormNode';
import { useInvalidateFlowsAndUser } from './useInvalidateFlowsAndUser';

/*
React Component to render a Kratos UiContainer and hook up submit/update.

Typically used by kratos/Flow to provide generic UI handling based on
Kratos UiNodes returned by the API.

Flow's Parent must provide a submitter function that, given node values,
makes a submit request to Kratos, eg via Ory's SDK.  The node values are
stored in the Form as React controlled values, but default-to/are-resynced
to whatever values are stored in the Kratos Flow returned on flow 
init/get/submit requests.  Kratos group/node names are used as lookup-keys.
See @ory/kratos-client/api.ts, and in particular the submit body types, to
get an idea what group/node names are used by a particular flow.

See Flow for big-picture documentation.
*/
export default function Form(props: FormProps)
{
  const invalidateFlowsAndUser = useInvalidateFlowsAndUser();
  const nodes = props.uiNodes;
  const defaultNodeValues = new Map();
  for (const node of nodes) {
    defaultNodeValues.set(getNodeKey(node), getNodeInitialValue(node));
  }
  
  const [nodeValues, setNodeValues] = React.useState(defaultNodeValues);


  function setNodeValue(key: string, value: any) {
    const updatedMap = new Map(nodeValues);
    updatedMap.set(key, value);
    setNodeValues(updatedMap);
  }

  const queryClient = useQueryClient();


  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    // hacky types due to native event use, but don't know a better way
    // to get the submitter (button's) value in React?
    const submitter: string | undefined = (
        (e.nativeEvent as any)?.submitter?.value
    );
    if (submitter === undefined) {
      throw Error('assert: native form submitters should have values');
    }

    const submitGroup = props.submitGroup;

    try {
      // once tried to tweak the submit to override options.responseType
      // so that Kratos would give the return_to redirect, but it didn't
      // seem to work.. probably easiest to handle it ourselves afterwards

      // TODO: this should be wrapped in a react-query-mutate probably..
      const response = await props.flowSubmitter(
        nodeValues,
        submitGroup,
        submitter
      );

      if (response.status === 200) {
        await invalidateFlowsAndUser();
        if (props.afterSuccessfulSubmit) {
          await props.afterSuccessfulSubmit();
        }
      }
    }
    catch (error: any) {
      let swallow = false;
      const kratosError = error.response.data.error;

      if (kratosError?.code === 403) {
        if (kratosError.id === 'session_refresh_required') {
          swallow = true;
          props.loginRefresher();
       }
      }

      if (kratosError?.id === 'browser_location_change_required') {
        const redirect_url = error.response.data?.redirect_browser_to;
        window.location.assign(redirect_url);
      }

      // we swallow these errors to allow Kratos to give feedback on the
      // next flow refetch:
      const maybeInvalidFormData = error.response.status === 400;
      if (maybeInvalidFormData) {
        swallow = true;
      } 

      if (!swallow) {
        throw error;
      }
    }

    // invalidate query, in case errored, want to refetch!
    queryClient.invalidateQueries({
      queryKey: ['kratos', 'flow', 'get', props.flowType]
    });
  }

  function makeNode(node: UiNode) {
    const key = getNodeKey(node);
    const initialValue = getNodeInitialValue(node);
    const localValue = nodeValues.get(key);
    const value = localValue === undefined ? initialValue : localValue;
    return (<FormNode
      key={key}
      node={node}
      value={value}
      valueSetter={setNodeValue}
    />);
  }

  return (
    <form onSubmit={onSubmit}>
      <Stack>
        { nodes.map(makeNode) }
      </Stack>
    </form>);
}


interface FormProps {
  uiNodes: UiNode[];
  submitGroup: string;
  flowId: string;
  flowType: string; // eg "registration", used as reactQuery key

  /*  Called on submit.  Callee should build a body and submit to server,
      eg via Ory SDK.  */
  flowSubmitter: (
      values: BodyValues, submitGroup: string, submitter: string
  ) => Promise<SubmitResponse>;

  /*  When a flow form is submitted after the user's session has gone stale,
      Kratos errors with a 403, adding the session_refresh_required code.
      The following callback is called in these situations, and can be used to,
      eg, render a nested refresh login UI. See Flow.needsRefreshLogin.  */
  loginRefresher: () => void;

  // optional callback for eg post-login/registration redirects
  afterSuccessfulSubmit?: () => void;
}
